/**
 * Simple API Service - Clean, Working Endpoints
 * ==============================================
 * These endpoints bypass the complex ORM and connect to the /simple/ API
 */

import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Create axios instance for simple API
const simpleClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1/simple`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Types
export interface Product {
  id: string;
  sku: string;
  name: string;
  description?: string;
  category: string;
  
  // Pricing
  price: number;
  cost: number;
  landed_cost: number;
  holding_cost_per_unit: number;
  
  // Inventory Management
  stock: number;
  safety_stock: number;
  reorder_point: number;
  reorder_quantity: number;
  max_stock: number;
  
  // Location & Storage
  warehouse_id?: string;
  bin_location?: string;
  
  // Physical Attributes
  unit_of_measure: string;
  weight_kg: number;
  length_cm: number;
  width_cm: number;
  height_cm: number;
  is_hazardous: boolean;
  storage_conditions?: string;
  
  // Supplier Info
  primary_supplier_id?: string;
  supplier_sku?: string;
  supplier_lead_time_days: number;
  
  // Demand & Forecasting
  avg_daily_demand: number;
  demand_variability: number;
  seasonality_factor: number;
  
  // Traceability
  lot_tracking_enabled: boolean;
  serial_tracking_enabled: boolean;
  expiration_tracking: boolean;
  shelf_life_days?: number;
  
  // Compliance & Classification
  hs_code?: string;
  country_of_origin?: string;
  quality_grade: string;
  certifications?: string;
  
  // Status & Timestamps
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ProductCreate {
  name: string;
  sku?: string;
  description?: string;
  category?: string;
  price?: number;
  cost?: number;
  landed_cost?: number;
  stock?: number;
  safety_stock?: number;
  reorder_point?: number;
  reorder_quantity?: number;
  max_stock?: number;
  warehouse_id?: string;
  bin_location?: string;
  unit_of_measure?: string;
  weight_kg?: number;
  length_cm?: number;
  width_cm?: number;
  height_cm?: number;
  is_hazardous?: boolean;
  storage_conditions?: string;
  primary_supplier_id?: string;
  supplier_sku?: string;
  supplier_lead_time_days?: number;
  avg_daily_demand?: number;
  lot_tracking_enabled?: boolean;
  serial_tracking_enabled?: boolean;
  expiration_tracking?: boolean;
  shelf_life_days?: number;
  hs_code?: string;
  country_of_origin?: string;
  quality_grade?: string;
  certifications?: string;
  status?: string;
}

export interface Supplier {
  id: string;
  name: string;
  code: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  status: string;
  lead_time_days: number;
  reliability_score: number;
  created_at: string;
  updated_at: string;
}

export interface SupplierCreate {
  name: string;
  code?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  lead_time_days?: number;
  reliability_score?: number;
}

export interface Alert {
  id: string;
  alert_type: string;
  severity: string;
  title: string;
  description?: string;
  affected_products?: string[];
  affected_suppliers?: string[];
  status: string;
  created_at: string;
  resolved_at?: string;
}

export interface Agent {
  id: string;
  name: string;
  type: string;
  description: string;
  capabilities: string[];
  status: string;
  last_run?: string;
  run_count: number;
  avg_confidence: number;
  created_at: string;
}

export interface ForecastResult {
  agent: string;
  type: string;
  confidence: number;
  predictions?: any[];
  optimizations?: any[];
  insights?: string[];
  recommendations: string[];
  generated_at: string;
}

export interface DashboardMetrics {
  products: {
    total: number;
    active: number;
    low_stock: number;
  };
  suppliers: {
    total: number;
    avg_reliability: number;
  };
  inventory: {
    total_value: number;
    low_stock_count: number;
  };
  alerts: {
    open: number;
    critical: number;
  };
  resilience: {
    score: number;
    status: string;
    trend: string;
    factors: {
      inventory_health: number;
      supplier_reliability: number;
      alert_severity: number;
    };
  };
  updated_at: string;
}

export interface ChatResponse {
  session_id: string;
  message: string;
  agent_used: string;
  agent_id: string;
  details: ForecastResult;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
}

// New Supply Chain Types
export interface Component {
  id: string;
  component_id: string;
  name: string;
  description?: string;
  category: string;
  unit_of_measure: string;
  unit_cost: number;
  current_stock: number;
  safety_stock: number;
  reorder_point: number;
  lead_time_days: number;
  primary_supplier_id?: string;
  alternative_supplier_ids?: string;
  country_of_origin?: string;
  hs_code?: string;
  is_critical: boolean;
  substitute_component_ids?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface ComponentCreate {
  name: string;
  component_id?: string;
  description?: string;
  category?: string;
  unit_of_measure?: string;
  unit_cost?: number;
  current_stock?: number;
  safety_stock?: number;
  reorder_point?: number;
  lead_time_days?: number;
  primary_supplier_id?: string;
  country_of_origin?: string;
  hs_code?: string;
  is_critical?: boolean;
}

export interface Warehouse {
  id: string;
  warehouse_id: string;
  name: string;
  warehouse_type: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  total_capacity: number;
  current_utilization: number;
  manager_name?: string;
  contact_email?: string;
  contact_phone?: string;
  operating_hours?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface WarehouseCreate {
  name: string;
  warehouse_id?: string;
  warehouse_type?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  total_capacity?: number;
  manager_name?: string;
  contact_email?: string;
  contact_phone?: string;
  operating_hours?: string;
}

export interface GeocodeResult {
  query: string;
  formatted_address: string;
  latitude: number;
  longitude: number;
  provider: string;
  confidence?: number;
}

export interface BOMItem {
  id: string;
  product_id: string;
  component_id: string;
  quantity_required: number;
  unit_of_measure: string;
  position_in_assembly?: string;
  is_optional: boolean;
  created_at: string;
}

export interface BOMItemCreate {
  component_id: string;
  quantity_required: number;
  unit_of_measure?: string;
  position_in_assembly?: string;
  is_optional?: boolean;
}

export interface PurchaseOrder {
  id: string;
  po_number: string;
  supplier_id: string;
  warehouse_id?: string;
  status: string;
  order_date: string;
  expected_delivery?: string;
  actual_delivery?: string;
  subtotal: number;
  tax: number;
  shipping_cost: number;
  total_amount: number;
  payment_terms?: string;
  notes?: string;
  created_by?: string;
  approved_by?: string;
  created_at: string;
  updated_at: string;
  items?: PurchaseOrderItem[];
}

export interface PurchaseOrderItem {
  id: string;
  purchase_order_id: string;
  product_id?: string;
  component_id?: string;
  quantity_ordered: number;
  unit_price: number;
  total_price: number;
  quantity_received: number;
}

export interface PurchaseOrderCreate {
  supplier_id: string;
  warehouse_id?: string;
  expected_delivery?: string;
  payment_terms?: string;
  notes?: string;
  items: {
    product_id?: string;
    component_id?: string;
    quantity_ordered: number;
    unit_price: number;
  }[];
}

export interface InventoryTransaction {
  id: string;
  transaction_type: string;
  product_id?: string;
  component_id?: string;
  warehouse_id?: string;
  quantity: number;
  unit_cost?: number;
  reference_type?: string;
  reference_id?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
}

export interface InventoryTransactionCreate {
  transaction_type: string;
  product_id?: string;
  component_id?: string;
  warehouse_id?: string;
  quantity: number;
  unit_cost?: number;
  reference_type?: string;
  reference_id?: string;
  notes?: string;
}

export interface ReorderSuggestion {
  product_id: string;
  product_name: string;
  sku: string;
  current_stock: number;
  reorder_point: number;
  suggested_quantity: number;
  supplier_id?: string;
  reason: string;
}

// Simple API Service
export const simpleApi = {
  // Products
  products: {
    list: async (params?: { skip?: number; limit?: number; status?: string; category?: string; search?: string }): Promise<PaginatedResponse<Product>> => {
      const response = await simpleClient.get('/products', { params });
      return response.data;
    },
    
    get: async (id: string): Promise<Product> => {
      const response = await simpleClient.get(`/products/${id}`);
      return response.data;
    },
    
    create: async (data: ProductCreate): Promise<Product> => {
      const response = await simpleClient.post('/products', data);
      return response.data;
    },
    
    update: async (id: string, data: Partial<ProductCreate>): Promise<Product> => {
      const response = await simpleClient.put(`/products/${id}`, data);
      return response.data;
    },
    
    delete: async (id: string): Promise<void> => {
      await simpleClient.delete(`/products/${id}`);
    },
  },

  // Suppliers
  suppliers: {
    list: async (params?: { skip?: number; limit?: number; status?: string }): Promise<PaginatedResponse<Supplier>> => {
      const response = await simpleClient.get('/suppliers', { params });
      return response.data;
    },
    
    get: async (id: string): Promise<Supplier> => {
      const response = await simpleClient.get(`/suppliers/${id}`);
      return response.data;
    },
    
    create: async (data: SupplierCreate): Promise<Supplier> => {
      const response = await simpleClient.post('/suppliers', data);
      return response.data;
    },
    
    update: async (id: string, data: Partial<SupplierCreate>): Promise<Supplier> => {
      const response = await simpleClient.put(`/suppliers/${id}`, data);
      return response.data;
    },
    
    delete: async (id: string): Promise<void> => {
      await simpleClient.delete(`/suppliers/${id}`);
    },
  },

  // Alerts
  alerts: {
    list: async (params?: { status?: string; severity?: string; limit?: number }): Promise<Alert[]> => {
      const response = await simpleClient.get('/alerts', { params });
      return response.data;
    },
    
    create: async (data: { alert_type: string; title: string; description?: string; severity?: string }): Promise<Alert> => {
      const response = await simpleClient.post('/alerts', data);
      return response.data;
    },
    
    resolve: async (id: string): Promise<void> => {
      await simpleClient.post(`/alerts/${id}/resolve`);
    },
  },

  // Dashboard
  dashboard: {
    getMetrics: async (): Promise<DashboardMetrics> => {
      const response = await simpleClient.get('/dashboard');
      return response.data;
    },
    
    getResilience: async () => {
      const response = await simpleClient.get('/dashboard/resilience');
      return response.data;
    },
  },

  // AI Agents
  agents: {
    list: async (): Promise<{ items: Agent[]; total: number }> => {
      const response = await simpleClient.get('/agents');
      return response.data;
    },
    
    get: async (id: string): Promise<Agent> => {
      const response = await simpleClient.get(`/agents/${id}`);
      return response.data;
    },
    
    runForecast: async (agentId: string): Promise<ForecastResult> => {
      const response = await simpleClient.post(`/agents/${agentId}/forecast`);
      return response.data;
    },
    
    getCapabilities: async (agentId: string) => {
      const response = await simpleClient.get(`/agents/${agentId}/capabilities`);
      return response.data;
    },
  },

  // Chat
  chat: {
    send: async (message: string, sessionId?: string): Promise<ChatResponse> => {
      const params: any = { message };
      if (sessionId) params.session_id = sessionId;
      const response = await simpleClient.post('/chat', null, { params });
      return response.data;
    },
  },

  // Components (NEW)
  components: {
    list: async (params?: { skip?: number; limit?: number; status?: string; category?: string }): Promise<PaginatedResponse<Component>> => {
      const response = await simpleClient.get('/components', { params });
      return response.data;
    },
    
    get: async (id: string): Promise<Component> => {
      const response = await simpleClient.get(`/components/${id}`);
      return response.data;
    },
    
    create: async (data: ComponentCreate): Promise<Component> => {
      const response = await simpleClient.post('/components', data);
      return response.data;
    },
    
    update: async (id: string, data: Partial<ComponentCreate>): Promise<Component> => {
      const response = await simpleClient.put(`/components/${id}`, data);
      return response.data;
    },
    
    delete: async (id: string): Promise<void> => {
      await simpleClient.delete(`/components/${id}`);
    },
  },

  // Warehouses (NEW)
  warehouses: {
    list: async (params?: { skip?: number; limit?: number; status?: string }): Promise<PaginatedResponse<Warehouse>> => {
      const response = await simpleClient.get('/warehouses', { params });
      return response.data;
    },
    
    get: async (id: string): Promise<Warehouse> => {
      const response = await simpleClient.get(`/warehouses/${id}`);
      return response.data;
    },
    
    create: async (data: WarehouseCreate): Promise<Warehouse> => {
      const response = await simpleClient.post('/warehouses', data);
      return response.data;
    },
    
    update: async (id: string, data: Partial<WarehouseCreate>): Promise<Warehouse> => {
      const response = await simpleClient.put(`/warehouses/${id}`, data);
      return response.data;
    },
    
    delete: async (id: string): Promise<void> => {
      await simpleClient.delete(`/warehouses/${id}`);
    },
  },

  geocoding: {
    geocode: async (address: string, provider: 'auto' | 'nominatim' | 'google' = 'auto'): Promise<GeocodeResult> => {
      const response = await apiClient.post('/geocoding/geocode', { address, provider });
      return response.data.result;
    },
  },

  // Bill of Materials (NEW)
  bom: {
    getProductBOM: async (productId: string): Promise<BOMItem[]> => {
      const response = await simpleClient.get(`/products/${productId}/bom`);
      return response.data;
    },
    
    addItem: async (productId: string, data: BOMItemCreate): Promise<BOMItem> => {
      const response = await simpleClient.post(`/products/${productId}/bom`, data);
      return response.data;
    },
    
    deleteItem: async (productId: string, bomItemId: string): Promise<void> => {
      await simpleClient.delete(`/products/${productId}/bom/${bomItemId}`);
    },
  },

  // Purchase Orders (NEW)
  purchaseOrders: {
    list: async (params?: { skip?: number; limit?: number; status?: string }): Promise<PaginatedResponse<PurchaseOrder>> => {
      const response = await simpleClient.get('/purchase-orders', { params });
      return response.data;
    },
    
    get: async (id: string): Promise<PurchaseOrder> => {
      const response = await simpleClient.get(`/purchase-orders/${id}`);
      return response.data;
    },
    
    create: async (data: PurchaseOrderCreate): Promise<PurchaseOrder> => {
      const response = await simpleClient.post('/purchase-orders', data);
      return response.data;
    },
    
    updateStatus: async (id: string, status: string): Promise<PurchaseOrder> => {
      const response = await simpleClient.put(`/purchase-orders/${id}/status`, { status });
      return response.data;
    },
  },

  // Inventory Transactions (NEW)
  inventory: {
    getTransactions: async (params?: { skip?: number; limit?: number; transaction_type?: string }): Promise<PaginatedResponse<InventoryTransaction>> => {
      const response = await simpleClient.get('/inventory/transactions', { params });
      return response.data;
    },
    
    createTransaction: async (data: InventoryTransactionCreate): Promise<InventoryTransaction> => {
      const response = await simpleClient.post('/inventory/transactions', data);
      return response.data;
    },
    
    // Get inventory levels (uses products with stock info)
    getLevels: async (params?: { skip?: number; limit?: number; status?: string }): Promise<PaginatedResponse<Product>> => {
      const response = await simpleClient.get('/products', { params });
      return response.data;
    },
  },

  // Reordering Suggestions (NEW)
  reordering: {
    getSuggestions: async (): Promise<ReorderSuggestion[]> => {
      const response = await simpleClient.get('/reordering/suggestions');
      return response.data;
    },
  },
};

export default simpleApi;
